import type {Chart as HighchartsChart, SVGElement} from "highcharts";

import {priceM2Format} from "@pg-mono/string-utils";

import {TrendSeriesPoint} from "../../../types/TrendSeriesPoint";
import {
    ACTIVE_BAR_OUTLINE_PADDING,
    ACTIVE_BAR_OUTLINE_RADIUS,
    ACTIVE_BAR_OUTLINE_STROKE_WIDTH,
    CHART_PLOT_HEIGHT,
    TRANSACTIONS_BAR_GAP,
    TRANSACTIONS_BAR_MAX_HEIGHT,
    TRANSACTIONS_BAR_MAX_WIDTH,
    TRANSACTIONS_BAR_MIN_HEIGHT,
    TRANSACTIONS_BAR_MIN_WIDTH,
    TRANSACTIONS_BAR_OPACITY_ACTIVE,
    TRANSACTIONS_BAR_OPACITY_PRELIMINARY,
    TRANSACTIONS_BAR_WIDTH_RATIO,
    TRANSACTIONS_LABEL_OFFSET,
    TRANSACTIONS_NUMBER_FONT_SIZE_DESKTOP,
    TRANSACTIONS_NUMBER_FONT_SIZE_MOBILE,
    TRANSACTIONS_NUMBER_LINE_HEIGHT,
    TRANSACTIONS_NUMBER_MIN_SPACING,
    TRANSACTIONS_NUMBER_OFFSET
} from "../constants/chart_constants";
import {NO_DATA_LABEL} from "../prices_trend_format";

export interface PreliminaryBandRect {
    left: number;
    width: number;
    top: number;
}

interface TransactionBarRect {
    x: number;
    top: number;
    width: number;
    height: number;
}

export interface ChartWithTrendExtras extends HighchartsChart {
    trendTransactionCounts?: SVGElement;
    trendTransactionBarRects?: Map<number, TransactionBarRect>;
    trendActiveBarOutline?: SVGElement;
}

export interface RcnPointCustom {
    count: number;
}

export const toHighchartsData = (series: TrendSeriesPoint[]): [number, number][] => series.map((point) => [new Date(point.date).getTime(), point.value]);

export const toRcnHighchartsData = (series: TrendSeriesPoint[]): {x: number; y: number; custom: RcnPointCustom}[] =>
    series.map((point) => ({x: new Date(point.date).getTime(), y: point.value, custom: {count: point.count ?? 0}}));

export const getTooltipPillHtml = (text: string): string =>
    `<span style="display:inline-flex;align-items:center;padding:0.4rem 1.2rem;border-radius:999px;background:var(--colors-beige-subtle);font-size:1.106rem;font-weight:600;line-height:1.2rem;color:var(--colors-secondary);white-space:nowrap;">${text}</span>`;

export const getTooltipRowHtml = (row: {color: string; label: string; value: number | null; description?: string}): string => `
    <div>
        <div style="display:flex;align-items:center;justify-content:space-between;gap:1.2rem;">
            <span style="display:flex;align-items:center;gap:0.6rem;font-size:1.4rem;font-weight:400;line-height:2rem;color:var(--colors-secondary);">
                <span style="width:0.8rem;height:0.8rem;border-radius:50%;background:${row.color};flex-shrink:0;"></span>
                ${row.label}
            </span>
            <b style="white-space:nowrap;font-size:1.4rem;font-weight:600;line-height:2rem;color:var(--colors-secondary);">${row.value === null ? NO_DATA_LABEL : priceM2Format(row.value, {precision: 0})}</b>
        </div>
        ${row.description ? `<div style="margin-left:1.4rem;margin-top:0.2rem;font-size:1.106rem;font-weight:400;line-height:1.2rem;color:var(--colors-gray-600);">${row.description}</div>` : ""}
    </div>
`;

// Anchored to the first preliminary point (not dashedFromDate) so the band and guide line align pixel-exact;
// the right edge fills to the chart's own edge (getPreliminaryBandRect), not just the last point.
export const getPreliminaryFrom = (
    rpSeries: TrendSeriesPoint[] | null,
    rcnSeries: TrendSeriesPoint[],
    dashedFromTimestamp: number | undefined
): number | null => {
    if (dashedFromTimestamp === undefined) {
        return null;
    }

    const preliminaryTimestamps = [...(rpSeries ?? []), ...rcnSeries]
        .map((point) => new Date(point.date).getTime())
        .filter((timestamp) => timestamp >= dashedFromTimestamp);

    return preliminaryTimestamps.length > 0 ? Math.min(...preliminaryTimestamps) : null;
};

export const getPreliminaryBandRect = (chart: HighchartsChart, preliminaryFrom: number | null): PreliminaryBandRect | null => {
    if (preliminaryFrom === null) {
        return null;
    }

    const left = chart.xAxis[0].toPixels(preliminaryFrom, false);
    const right = chart.plotLeft + chart.plotWidth;

    return right > left ? {left, width: right - left, top: chart.plotTop} : null;
};

// Drawn on the chart's own SVG (not an HTML row) so columns stay aligned with the price line's ticks -
// a row outside the chart couldn't track the y-axis label width shifting the plot's left offset.
export const drawTransactionCounts = (chart: ChartWithTrendExtras, rcnSeries: TrendSeriesPoint[], preliminaryFrom: number | null, isMobile: boolean) => {
    chart.trendTransactionCounts?.destroy();
    chart.trendTransactionCounts = undefined;
    // Bar positions are about to be recomputed - clear any outline drawn for the previous position.
    chart.trendActiveBarOutline?.destroy();
    chart.trendActiveBarOutline = undefined;

    const points = rcnSeries.filter((point): point is TrendSeriesPoint & {count: number} => typeof point.count === "number");
    const barRects = new Map<number, TransactionBarRect>();
    chart.trendTransactionBarRects = barRects;

    if (points.length === 0) {
        return;
    }

    const group = chart.renderer.g("prices-trend-transaction-counts").add();
    const maxCount = Math.max(...points.map((point) => point.count), 1);
    const labelBaseline = CHART_PLOT_HEIGHT + TRANSACTIONS_LABEL_OFFSET;
    const numberBaseline = labelBaseline + TRANSACTIONS_NUMBER_OFFSET;
    const barsBottom = numberBaseline + TRANSACTIONS_BAR_GAP + TRANSACTIONS_BAR_MAX_HEIGHT;
    const pointSpacing = chart.plotWidth / points.length;
    const barWidth = Math.min(TRANSACTIONS_BAR_MAX_WIDTH, Math.max(TRANSACTIONS_BAR_MIN_WIDTH, pointSpacing * TRANSACTIONS_BAR_WIDTH_RATIO));
    const numberFontSize = isMobile ? TRANSACTIONS_NUMBER_FONT_SIZE_MOBILE : TRANSACTIONS_NUMBER_FONT_SIZE_DESKTOP;
    // Too tight to fit without colliding - skip every other number; bars still render for every month.
    const showEveryNumber = pointSpacing >= TRANSACTIONS_NUMBER_MIN_SPACING;

    chart.renderer.text("Transakcje w RCN", chart.plotLeft, labelBaseline).css({fontSize: "1.106rem", color: "var(--colors-gray-600)"}).add(group);

    points.forEach((point, index) => {
        const timestamp = new Date(point.date).getTime();
        const x = chart.xAxis[0].toPixels(timestamp, false);
        // > not >= - the boundary point itself is still solid (matches the line's zone split, whose `value`
        // is its last point); only points strictly after it are preliminary.
        const isPreliminary = preliminaryFrom !== null && timestamp > preliminaryFrom;
        const textColor = isPreliminary ? "var(--colors-gray-500)" : "var(--colors-gray-700)";
        const barOpacity = isPreliminary ? TRANSACTIONS_BAR_OPACITY_PRELIMINARY : TRANSACTIONS_BAR_OPACITY_ACTIVE;
        const barHeight = TRANSACTIONS_BAR_MIN_HEIGHT + (point.count / maxCount) * (TRANSACTIONS_BAR_MAX_HEIGHT - TRANSACTIONS_BAR_MIN_HEIGHT);

        if (showEveryNumber || index % 2 === 0) {
            chart.renderer
                .text(String(point.count), x, numberBaseline)
                .attr({align: "center"})
                .css({fontSize: numberFontSize, fontWeight: "600", lineHeight: TRANSACTIONS_NUMBER_LINE_HEIGHT, color: textColor})
                .add(group);
        }

        chart.renderer
            .rect(x - barWidth / 2, barsBottom - barHeight, barWidth, barHeight, 2)
            .attr({fill: "var(--colors-secondary)", "fill-opacity": barOpacity})
            .add(group);

        barRects.set(timestamp, {x, top: barsBottom - barHeight, width: barWidth, height: barHeight});
    });

    chart.trendTransactionCounts = group;
};

// Draws/clears the active bar's outline on hover/tap - separate from drawTransactionCounts's group, which only updates on data/resize.
export const setActiveTransactionBar = (chart: ChartWithTrendExtras, timestamp: number | null) => {
    chart.trendActiveBarOutline?.destroy();
    chart.trendActiveBarOutline = undefined;

    const rect = timestamp !== null ? chart.trendTransactionBarRects?.get(timestamp) : undefined;

    if (!rect) {
        return;
    }

    chart.trendActiveBarOutline = chart.renderer
        .rect(
            rect.x - rect.width / 2 - ACTIVE_BAR_OUTLINE_PADDING,
            rect.top - ACTIVE_BAR_OUTLINE_PADDING,
            rect.width + ACTIVE_BAR_OUTLINE_PADDING * 2,
            rect.height + ACTIVE_BAR_OUTLINE_PADDING * 2,
            ACTIVE_BAR_OUTLINE_RADIUS
        )
        .attr({fill: "none", stroke: "var(--colors-secondary)", "stroke-width": ACTIVE_BAR_OUTLINE_STROKE_WIDTH, zIndex: 6})
        .add();
};
import type {Chart as HighchartsChart, SVGElement} from "highcharts";

import {priceM2Format} from "@pg-mono/string-utils";

import {TrendSeriesPoint} from "../../../types/TrendSeriesPoint";
import {
    ACTIVE_BAR_OUTLINE_PADDING,
    ACTIVE_BAR_OUTLINE_RADIUS,
    ACTIVE_BAR_OUTLINE_STROKE_WIDTH,
    CHART_PLOT_HEIGHT,
    TRANSACTIONS_BAR_GAP,
    TRANSACTIONS_BAR_MAX_HEIGHT,
    TRANSACTIONS_BAR_MAX_WIDTH,
    TRANSACTIONS_BAR_MIN_HEIGHT,
    TRANSACTIONS_BAR_MIN_WIDTH,
    TRANSACTIONS_BAR_OPACITY_ACTIVE,
    TRANSACTIONS_BAR_OPACITY_PRELIMINARY,
    TRANSACTIONS_BAR_WIDTH_RATIO,
    TRANSACTIONS_LABEL_OFFSET,
    TRANSACTIONS_NUMBER_FONT_SIZE_DESKTOP,
    TRANSACTIONS_NUMBER_FONT_SIZE_MOBILE,
    TRANSACTIONS_NUMBER_LINE_HEIGHT,
    TRANSACTIONS_NUMBER_MIN_SPACING,
    TRANSACTIONS_NUMBER_OFFSET
} from "../constants/chart_constants";
import {NO_DATA_LABEL} from "../prices_trend_format";

export interface PreliminaryBandRect {
    left: number;
    width: number;
    top: number;
}

interface TransactionBarRect {
    x: number;
    top: number;
    width: number;
    height: number;
}

export interface ChartWithTrendExtras extends HighchartsChart {
    trendTransactionCounts?: SVGElement;
    trendTransactionBarRects?: Map<number, TransactionBarRect>;
    trendActiveBarOutline?: SVGElement;
}

export interface RcnPointCustom {
    count: number;
}

export const toHighchartsData = (series: TrendSeriesPoint[]): [number, number][] => series.map((point) => [new Date(point.date).getTime(), point.value]);

export const toRcnHighchartsData = (series: TrendSeriesPoint[]): {x: number; y: number; custom: RcnPointCustom}[] =>
    series.map((point) => ({x: new Date(point.date).getTime(), y: point.value, custom: {count: point.count ?? 0}}));

export const getTooltipPillHtml = (text: string): string =>
    `<span style="display:inline-flex;align-items:center;padding:0.4rem 1.2rem;border-radius:999px;background:var(--colors-beige-subtle);font-size:1.106rem;font-weight:600;line-height:1.2rem;color:var(--colors-secondary);white-space:nowrap;">${text}</span>`;

export const getTooltipRowHtml = (row: {color: string; label: string; value: number | null; description?: string}): string => `
    <div>
        <div style="display:flex;align-items:center;justify-content:space-between;gap:1.2rem;">
            <span style="display:flex;align-items:center;gap:0.6rem;font-size:1.4rem;font-weight:400;line-height:2rem;color:var(--colors-secondary);">
                <span style="width:0.8rem;height:0.8rem;border-radius:50%;background:${row.color};flex-shrink:0;"></span>
                ${row.label}
            </span>
            <b style="white-space:nowrap;font-size:1.4rem;font-weight:600;line-height:2rem;color:var(--colors-secondary);">${row.value === null ? NO_DATA_LABEL : priceM2Format(row.value, {precision: 0})}</b>
        </div>
        ${row.description ? `<div style="margin-left:1.4rem;margin-top:0.2rem;font-size:1.106rem;font-weight:400;line-height:1.2rem;color:var(--colors-gray-600);">${row.description}</div>` : ""}
    </div>
`;

// Anchored to the first preliminary point (not dashedFromDate) so the band and guide line align pixel-exact;
// the right edge fills to the chart's own edge (getPreliminaryBandRect), not just the last point.
export const getPreliminaryFrom = (
    rpSeries: TrendSeriesPoint[] | null,
    rcnSeries: TrendSeriesPoint[],
    dashedFromTimestamp: number | undefined
): number | null => {
    if (dashedFromTimestamp === undefined) {
        return null;
    }

    const preliminaryTimestamps = [...(rpSeries ?? []), ...rcnSeries]
        .map((point) => new Date(point.date).getTime())
        .filter((timestamp) => timestamp >= dashedFromTimestamp);

    return preliminaryTimestamps.length > 0 ? Math.min(...preliminaryTimestamps) : null;
};

export const getPreliminaryBandRect = (chart: HighchartsChart, preliminaryFrom: number | null): PreliminaryBandRect | null => {
    if (preliminaryFrom === null) {
        return null;
    }

    const left = chart.xAxis[0].toPixels(preliminaryFrom, false);
    const right = chart.plotLeft + chart.plotWidth;

    return right > left ? {left, width: right - left, top: chart.plotTop} : null;
};

// Drawn on the chart's own SVG (not an HTML row) so columns stay aligned with the price line's ticks -
// a row outside the chart couldn't track the y-axis label width shifting the plot's left offset.
export const drawTransactionCounts = (chart: ChartWithTrendExtras, rcnSeries: TrendSeriesPoint[], preliminaryFrom: number | null, isMobile: boolean) => {
    chart.trendTransactionCounts?.destroy();
    chart.trendTransactionCounts = undefined;
    // Bar positions are about to be recomputed - clear any outline drawn for the previous position.
    chart.trendActiveBarOutline?.destroy();
    chart.trendActiveBarOutline = undefined;

    const points = rcnSeries.filter((point): point is TrendSeriesPoint & {count: number} => typeof point.count === "number");
    const barRects = new Map<number, TransactionBarRect>();
    chart.trendTransactionBarRects = barRects;

    if (points.length === 0) {
        return;
    }

    const group = chart.renderer.g("prices-trend-transaction-counts").add();
    const maxCount = Math.max(...points.map((point) => point.count), 1);
    const labelBaseline = CHART_PLOT_HEIGHT + TRANSACTIONS_LABEL_OFFSET;
    const numberBaseline = labelBaseline + TRANSACTIONS_NUMBER_OFFSET;
    const barsBottom = numberBaseline + TRANSACTIONS_BAR_GAP + TRANSACTIONS_BAR_MAX_HEIGHT;
    const pointSpacing = chart.plotWidth / points.length;
    const barWidth = Math.min(TRANSACTIONS_BAR_MAX_WIDTH, Math.max(TRANSACTIONS_BAR_MIN_WIDTH, pointSpacing * TRANSACTIONS_BAR_WIDTH_RATIO));
    const numberFontSize = isMobile ? TRANSACTIONS_NUMBER_FONT_SIZE_MOBILE : TRANSACTIONS_NUMBER_FONT_SIZE_DESKTOP;
    // Too tight to fit without colliding - skip every other number; bars still render for every month.
    const showEveryNumber = pointSpacing >= TRANSACTIONS_NUMBER_MIN_SPACING;

    chart.renderer.text("Transakcje w RCN", chart.plotLeft, labelBaseline).css({fontSize: "1.106rem", color: "var(--colors-gray-600)"}).add(group);

    points.forEach((point, index) => {
        const timestamp = new Date(point.date).getTime();
        const x = chart.xAxis[0].toPixels(timestamp, false);
        // > not >= - the boundary point itself is still solid (matches the line's zone split, whose `value`
        // is its last point); only points strictly after it are preliminary.
        const isPreliminary = preliminaryFrom !== null && timestamp > preliminaryFrom;
        const textColor = isPreliminary ? "var(--colors-gray-500)" : "var(--colors-gray-700)";
        const barOpacity = isPreliminary ? TRANSACTIONS_BAR_OPACITY_PRELIMINARY : TRANSACTIONS_BAR_OPACITY_ACTIVE;
        const barHeight = TRANSACTIONS_BAR_MIN_HEIGHT + (point.count / maxCount) * (TRANSACTIONS_BAR_MAX_HEIGHT - TRANSACTIONS_BAR_MIN_HEIGHT);

        if (showEveryNumber || index % 2 === 0) {
            chart.renderer
                .text(String(point.count), x, numberBaseline)
                .attr({align: "center"})
                .css({fontSize: numberFontSize, fontWeight: "600", lineHeight: TRANSACTIONS_NUMBER_LINE_HEIGHT, color: textColor})
                .add(group);
        }

        chart.renderer
            .rect(x - barWidth / 2, barsBottom - barHeight, barWidth, barHeight, 2)
            .attr({fill: "var(--colors-secondary)", "fill-opacity": barOpacity})
            .add(group);

        barRects.set(timestamp, {x, top: barsBottom - barHeight, width: barWidth, height: barHeight});
    });

    chart.trendTransactionCounts = group;
};

// Draws/clears the active bar's outline on hover/tap - separate from drawTransactionCounts's group, which only updates on data/resize.
export const setActiveTransactionBar = (chart: ChartWithTrendExtras, timestamp: number | null) => {
    chart.trendActiveBarOutline?.destroy();
    chart.trendActiveBarOutline = undefined;

    const rect = timestamp !== null ? chart.trendTransactionBarRects?.get(timestamp) : undefined;

    if (!rect) {
        return;
    }

    chart.trendActiveBarOutline = chart.renderer
        .rect(
            rect.x - rect.width / 2 - ACTIVE_BAR_OUTLINE_PADDING,
            rect.top - ACTIVE_BAR_OUTLINE_PADDING,
            rect.width + ACTIVE_BAR_OUTLINE_PADDING * 2,
            rect.height + ACTIVE_BAR_OUTLINE_PADDING * 2,
            ACTIVE_BAR_OUTLINE_RADIUS
        )
        .attr({fill: "none", stroke: "var(--colors-secondary)", "stroke-width": ACTIVE_BAR_OUTLINE_STROKE_WIDTH, zIndex: 6})
        .add();
};
